import { useEffect, useState } from "react";
import { ClipboardCheck, Save } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const ESTADOS = [
    { value: "presente", label: "Presente", style: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    { value: "tarde", label: "Tarde", style: "bg-amber-100 text-amber-800 border-amber-300" },
    { value: "ausente", label: "Ausente", style: "bg-red-100 text-red-800 border-red-300" },
];

function todayStr() {
    return new Date().toISOString().slice(0, 10);
}

export default function Asistencia() {
    const [sections, setSections] = useState([]);
    const [section, setSection] = useState("");
    const [date, setDate] = useState(todayStr());
    const [students, setStudents] = useState([]);
    const [marks, setMarks] = useState({});
    const [loadingSections, setLoadingSections] = useState(true);
    const [loadingRoster, setLoadingRoster] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/attendance/sections")
            .then((r) => {
                const list = r.data || [];
                setSections(list);
                if (list.length > 0) setSection(list[0]);
            })
            .catch(() => toast.error("Error cargando secciones"))
            .finally(() => setLoadingSections(false));
    }, []);

    useEffect(() => {
        if (!section || !date) return;
        setLoadingRoster(true);
        Promise.all([
            api.get("/attendance/students", { params: { section } }),
            api.get("/attendance", { params: { section, date } }),
        ])
            .then(([{ data: roster }, { data: existing }]) => {
                setStudents(roster || []);
                const map = {};
                (roster || []).forEach((s) => { map[s.id] = "presente"; });
                (existing || []).forEach((e) => { map[e.student_id] = e.estado; });
                setMarks(map);
            })
            .catch(() => toast.error("Error cargando la lista de la sección"))
            .finally(() => setLoadingRoster(false));
    }, [section, date]);

    function setMark(studentId, estado) {
        setMarks((prev) => ({ ...prev, [studentId]: estado }));
    }

    async function save() {
        setSaving(true);
        try {
            const entries = students.map((s) => ({ student_id: s.id, estado: marks[s.id] || "presente" }));
            await api.post("/attendance", { section, date, entries });
            toast.success("Asistencia guardada");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error guardando la asistencia");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="p-6 md:p-10 fade-in min-h-screen max-w-2xl mx-auto">
            <h1 className="text-2xl font-semibold mb-1 inline-flex items-center gap-2"><ClipboardCheck size={22} className="text-edu-navy" /> Asistencia</h1>
            <p className="text-edu-muted mb-6">Se guarda por sección y fecha — la ven todos los profesores de esa sección.</p>

            {loadingSections ? (
                <p className="text-sm text-edu-muted">Cargando secciones…</p>
            ) : sections.length === 0 ? (
                <div className="edu-card p-8 text-center text-edu-muted">No tienes cursos con secciones asignadas.</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div>
                            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Sección</span>
                            <select value={section} onChange={(e) => setSection(e.target.value)} className="edu-input w-full">
                                {sections.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <span className="text-xs text-edu-muted font-semibold uppercase tracking-widest mb-1 block">Fecha</span>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="edu-input w-full" />
                        </div>
                    </div>

                    {loadingRoster ? (
                        <p className="text-sm text-edu-muted">Cargando alumnos…</p>
                    ) : students.length === 0 ? (
                        <div className="edu-card p-8 text-center text-edu-muted">No hay alumnos en esta sección.</div>
                    ) : (
                        <>
                            <div className="space-y-2 mb-6">
                                {students.map((s) => (
                                    <div key={s.id} className="edu-card p-3 flex items-center justify-between gap-3">
                                        <p className="text-sm font-medium">{s.name}</p>
                                        <div className="flex gap-1.5 flex-shrink-0">
                                            {ESTADOS.map((e) => (
                                                <button
                                                    key={e.value}
                                                    onClick={() => setMark(s.id, e.value)}
                                                    className={`text-xs px-2.5 py-1.5 rounded-lg border ${marks[s.id] === e.value ? e.style : "border-edu text-edu-muted hover:bg-edu-soft"}`}
                                                >
                                                    {e.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end">
                                <button onClick={save} disabled={saving} className="edu-btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                                    <Save size={15} /> {saving ? "Guardando…" : "Guardar asistencia"}
                                </button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
